// @flow
import * as React from 'react';
import ReviewSection from './ReviewSection';
import ResponseSection from './ResponseSection';
import { QuestionSection, AnswerSection } from './QASection';
import { ImageSection, VideoSection } from './MediaSection';
import SocialCollectionSection from './SocialCollectionSection';
import { PwrUgcMetaInfo, ClientUgcMetaInfo } from './UgcMetaInfo';
import type { UGCSection } from './UGCSection';
import type {
  Review,
  Response,
  Image,
  Video,
  Question,
  Answer,
} from '../../models';
import CollapsedBlock from './CollapsedBlock';
import styles from './ContentSection.scss';

import {
  contentSection as container,
  contentSectionErrorMessage,
} from './ContentSection.scss';

const blockWhiteSpace = { margin: '2em' };
const SOCIAL_COLLECTION_SUBTYPE = 'SocialCollection';

type Props = {
  model: Review | Response | Image | Video | Question | Answer,
  program: AppProgram,
  hideMeta?: Boolean,
  children?: any,
};

type State = {
  metaExpanded: boolean,
};

const ErrorContent = ({ message }) => (
  <section className={container}>
    <div style={blockWhiteSpace}>
      <span className={contentSectionErrorMessage}>{message}</span>
    </div>
  </section>
);

const ContentTypeBadge = ({
  ugctype,
  visible,
}: {
  ugctype: UgcType,
  visible: boolean,
}) =>
  visible === true ? (
    <div className={styles.contentSectionContentTypeBadge}>
      <span className={styles.contentSectionContentTypeBadgeLabel}>
        Content Type:
      </span>
      <span
        className={styles.contentSectionContentTypeBadgeText}
        data-key="ugctype"
      >
        {ugctype}
      </span>
    </div>
  ) : (
    <span
      data-key="ugctype"
      aria-hidden="true"
      className={styles.contentSectionContentTypeBadgeTextHidden}
    >
      {ugctype}
    </span>
  );

// TODO view types
export default class ContentSection extends React.PureComponent<Props, State> {
  state = {
    metaExpanded: false,
  };
  collapsedContent: ?HTMLDivElement;
  scrollCollapsedContentIntoView: boolean;

  toggleMeta = (scrollIntoView?: boolean) => {
    this.scrollCollapsedContentIntoView = scrollIntoView === true;
    this.setState(prevState => ({ metaExpanded: !prevState.metaExpanded }));
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    // scroll collapsed content into view when expanding
    if (
      prevState.metaExpanded !== this.state.metaExpanded &&
      this.state.metaExpanded === true
    ) {
      this.scrollCollapsedContentIntoView &&
        this.collapsedContent &&
        this.collapsedContent.scrollIntoView();
    }
  }

  render() {
    const { model: ugc, program = 'client' } = this.props;
    const { metaExpanded } = this.state;
    let ComponentToRender;
    switch ((ugc.ugc_archetype: UgcType)) {
      case 'Review':
        ComponentToRender = ReviewSection;
        break;

      case 'BrandMerchantResponse':
      case 'MerchantResponse':
        ComponentToRender = ResponseSection;
        break;

      case 'Question':
        ComponentToRender = QuestionSection;
        break;

      case 'BrandAnswer':
      case 'Answer':
      case 'WDYB':
        ComponentToRender = AnswerSection;
        break;

      case 'Image':
        ComponentToRender =
          ugc.ugcsubtype === SOCIAL_COLLECTION_SUBTYPE
            ? SocialCollectionSection
            : ImageSection;
        break;

      case 'Video':
        ComponentToRender =
          ugc.ugcsubtype === SOCIAL_COLLECTION_SUBTYPE
            ? SocialCollectionSection
            : VideoSection;
        break;

      default:
        return (
          <ErrorContent
            message={`Could not render UGC #${ugc.ugc_id} of type: "${ugc.ugctype}"`}
          />
        );
    }

    const {
      Header,
      Content,
      getRelatedContent,
      provideCollapsedContent,
    } = (ComponentToRender: UGCSection);
    const options = { program };
    const relatedContent = getRelatedContent(ugc, options);
    const isStandardModerationView = program === ('pwr': AppProgram);

    let collapsedContent;
    if (provideCollapsedContent) {
      collapsedContent = provideCollapsedContent(ugc, options, {
        onClick: this.toggleMeta,
        open: metaExpanded,
      });
    }

    const MetaInfoComponent =
      isStandardModerationView === true ? PwrUgcMetaInfo : ClientUgcMetaInfo;

    if (!collapsedContent) {
      collapsedContent = (
        <CollapsedBlock
          label="Meta Info"
          open={metaExpanded}
          onClick={this.toggleMeta}
        >
          <MetaInfoComponent model={ugc} expanded={metaExpanded} />
        </CollapsedBlock>
      );
    }

    return (
      <section className={container}>
        <header className="mv3 mh4">
          <ContentTypeBadge
            ugctype={ugc.ugctype}
            visible={isStandardModerationView === true}
          />
          <Header ugc={ugc} options={options} />
        </header>
        <Content ugc={ugc} options={options} />
        {relatedContent && relatedContent.map(rc => rc)}
        {this.props.children}
        {this.props.hideMeta === true ? null : (
          <div ref={c => (this.collapsedContent = c)}>{collapsedContent}</div>
        )}
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/ContentSection/ContentSection.js